import {
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { formatDistanceToNow } from "date-fns";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { PerformerDTO } from "../../domain/contracts";

type PerformerRow = PerformerDTO;

type PerformerTableProps = {
  data: PerformerRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (row: PerformerRow) => void;
  onEdit: (row: PerformerRow) => void;
  onDelete: (id: string) => void;
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

export function PerformerTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
}: PerformerTableProps) {
  const columns: TanstackReactTable.ColumnDef<PerformerRow>[] = [
    {
      ...createSortableColumn("name", "ຊື່"),
      cell: ({ row }) => {
        const { name } = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground text-xs">
              {getInitials(name)}
            </div>
            <span className="font-medium">{name}</span>
          </div>
        );
      },
    },
    {
      id: "contactInfo",
      header: "ຂໍ້ມູນຕິດຕໍ່",
      cell: ({ row }) => {
        const { phoneNumber, email } = row.original;
        if (!phoneNumber && !email)
          return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-col gap-0.5">
            {phoneNumber && <span className="text-sm">{phoneNumber}</span>}
            {email && (
              <span className="text-muted-foreground text-xs">{email}</span>
            )}
          </div>
        );
      },
    },
    {
      id: "linkedInstructor",
      header: "ຄູຜູ້ສອນທີ່ຜູກ",
      cell: ({ row }) => {
        const name = row.original.instructorName;
        return name ? (
          <span className="text-sm">{name}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const active = row.original.status === "active";
        return (
          <div className="flex items-center gap-1.5">
            <span
              className={`h-2 w-2 rounded-full ${active ? "bg-green-500" : "bg-muted-foreground/40"}`}
            />
            <span className="text-sm">{active ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}</span>
          </div>
        );
      },
    },
    {
      id: "updatedAt",
      header: "ອັບເດດຫຼ້າສຸດ",
      cell: ({ row }) => {
        const d = row.original.updatedAt;
        if (!d) return "—";
        return (
          <span className="text-muted-foreground text-sm">
            {formatDistanceToNow(new Date(d), { addSuffix: true })}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "ຈັດການ",
      cell: ({ row }: { row: { original: PerformerRow } }) => {
        const { id } = row.original;
        return (
          <RowActions
            actions={[
              {
                label: "ເບິ່ງລາຍລະອຽດ",
                icon: <EyeIcon className="h-4 w-4" />,
                onClick: () => onView(row.original),
              },
              {
                label: "ແກ້ໄຂ",
                icon: <EditIcon className="h-4 w-4" />,
                onClick: () => onEdit(row.original),
              },
              {
                label: "ລຶບ",
                variant: "destructive" as const,
                icon: <TrashIcon className="h-4 w-4" />,
                onClick: async () => {
                  const ok = await confirm({
                    title: "ລຶບນັກສະແດງ",
                    description:
                      "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບນັກສະແດງນີ້? ຖ້ານັກສະແດງຜູກກັບງານສະແດງຢູ່ຈະລຶບບໍ່ໄດ້.",
                    actionText: "ລຶບ",
                    ActionProps: { variant: "destructive" },
                  });
                  if (ok) onDelete(id);
                },
              },
            ]}
            maxInline={2}
          />
        );
      },
    },
  ];

  return (
    <DataTable<PerformerRow, unknown>
      noDataMessage="ບໍ່ພົບນັກສະແດງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
